import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { AssistantToolCall } from "../types/assistant";
import { ChatSteps } from "./v2/ChatMessage";
import type { StepItem } from "./v2/ui/steps";

// German fallbacks, used when the host app's i18n has no `toolLabels.<name>`
// key (e.g. an older cached locale or a not-yet-translated tool name).
export const DEFAULT_TOOL_LABELS: Record<string, string> = {
  search: "Suche durchgeführt",
  search_content: "Inhalt durchsucht",
  search_tickets: "Tickets durchsucht",
  lookup_ticket: "Ticket geladen",
  search_database: "Datenbank durchsucht",
  query_database: "Datenbank abgefragt",
  // concept tools (ADR-0047), named after the tenant's catalog concept
  shop_offers: "Shop-Angebote nachgeschlagen",
  customer_service_history: "Servicehistorie abgerufen",
  machine_record: "Maschinendaten nachgeschlagen",
  travel_reports: "Reiseberichte durchsucht",
  customer_records: "Kundendaten nachgeschlagen",
  machine_maintenance: "Wartungsverträge abgerufen",
  machine_cases: "Hotline-Fälle zur Maschine abgerufen",
  case_thread: "Fallverlauf abgerufen",
  case_schedule: "Einsatzplan zum Fall abgerufen",
  rewrite_question: "Suche wird verfeinert",
  rewrite_answer: "Antwort wird umgeschrieben",
  edit_answer: "Antwort bearbeitet",
  list_documents: "Dokumentenliste abgerufen",
  read_document_content: "Dokument gelesen",
  ask_user_question: "Frage stellen",
  add_fact: "Fakt ergänzt",
  deep_search: "Erweiterte Suche durchgeführt",
  erp_lookup: "ERP-Datensätze gesucht",
  erp_update: "ERP-Datensatz aktualisiert",
  erp_create: "ERP-Datensatz angelegt",
  translate_answer: "Antwort übersetzt",
  explore_ontology: "Wissensgraph durchsucht",
  deep_research: "Tiefenrecherche",
  // the pydantic-ai chats' tools (ADR-0069)
  search_documents: "Dokumente durchsucht",
  search_past_cases: "Frühere Fälle durchsucht",
  find_documents: "Dokumente nach Namen gesucht",
  grep_documents: "Exakten Text in Dokumenten gesucht",
  grep_past_cases: "Exakten Text in früheren Fällen gesucht",
  read_past_case: "Früheren Fall gelesen",
  search_records: "Datensätze durchsucht",
  query_records: "Datensätze abgefragt",
  search_single_document: "In einem Dokument gesucht",
  read_document: "Dokument gelesen",
  find_document_by_name: "Dokument nach Namen gesucht",
  find_identifier: "Kennung gesucht",
  lookup_concept: "Begriff nachgeschlagen",
  // legacy labels kept for existing stored tool calls
  search_messages: "Nachrichten durchsucht",
  search_document_content: "Dokumentinhalt durchsucht",
};

/** "12 s" or "1:05 min". */
function formatElapsed(ms: number): string {
  const totalSec = Math.max(0, Math.floor(ms / 1000));
  if (totalSec < 60) return `${totalSec} s`;
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${String(s).padStart(2, "0")} min`;
}

/** Re-render once per second while `active` so running timers count up. */
function useSecondTick(active: boolean) {
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!active) return;
    const id = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, [active]);
}

/** The deep-research sub-agent's streamed reasoning, kept scrolled to the newest line. */
function DeepResearchThinking({ text, label }: { text: string; label: string }) {
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (ref.current) ref.current.scrollTop = ref.current.scrollHeight;
  }, [text]);
  return (
    // Focusable and named: keyboard users can scroll it.
    <div
      ref={ref}
      tabIndex={0}
      aria-label={label}
      className="max-h-20 overflow-y-auto whitespace-pre-wrap wrap-break-word"
    >
      {text}
    </div>
  );
}

/**
 * One turn's tool calls as the v2 "Arbeitsschritte" (ChatSteps). `live` while the
 * turn runs: the summary says it is working, steps show timers, and a running deep
 * research opens the list so its reasoning is visible. On a finished turn a step
 * still marked RUNNING was never closed, so it shows as skipped, not as a stale
 * spinner.
 */
export function ToolCallList({
  toolCalls,
  labels = DEFAULT_TOOL_LABELS,
  live = false,
}: {
  toolCalls: AssistantToolCall[];
  labels?: Record<string, string>;
  live?: boolean;
}) {
  const { t } = useTranslation();
  useSecondTick(live);

  // Timers are local wall-clock: stamped the first time a turn or step is seen running.
  const turnStart = useRef<number | null>(null);
  if (turnStart.current == null && toolCalls.length > 0) turnStart.current = Date.now();
  const startedAt = useRef<Record<string, number>>({});
  for (const tc of toolCalls) {
    if (tc.status === "RUNNING" && startedAt.current[tc.id] == null) {
      startedAt.current[tc.id] = Date.now();
    }
  }

  const deepRunning =
    live && toolCalls.some((tc) => tc.toolName === "deep_research" && tc.status === "RUNNING");
  const [open, setOpen] = useState(false);
  // Only ever opens by itself; the operator can still close it.
  useEffect(() => {
    if (deepRunning) setOpen(true);
  }, [deepRunning]);

  if (toolCalls.length === 0) return null;

  const thinkingLabel = t("toolLabels.deepResearchThinking", {
    defaultValue: "Zwischenstand der Tiefenrecherche",
  });

  const steps: StepItem[] = toolCalls.map((tc) => {
    // Prefer the host app's translated label; fall back to the German map.
    const label = t(`toolLabels.${tc.toolName}`, {
      defaultValue: labels[tc.toolName] ?? tc.toolName,
    });
    const running = tc.status === "RUNNING";
    let meta: string | undefined;
    // Timers only on the live turn: persisted history stays uncluttered.
    if (live) {
      if (running && startedAt.current[tc.id] != null) {
        meta = formatElapsed(Date.now() - startedAt.current[tc.id]);
      } else if (!running && tc.executionDurationMs != null) {
        meta = formatElapsed(tc.executionDurationMs);
      }
    }
    if (tc.status === "FAILED") return { id: tc.id, label, state: "failed", meta };
    if (running) {
      return live
        ? {
            id: tc.id,
            label,
            state: "running",
            meta,
            detail:
              tc.toolName === "deep_research" && tc.resultSummary ? (
                <DeepResearchThinking text={tc.resultSummary} label={thinkingLabel} />
              ) : undefined,
          }
        : {
            id: tc.id,
            label,
            state: "skipped",
            stateLabel: t("toolLabels.notFinished", { defaultValue: "nicht abgeschlossen" }),
          };
    }
    // "Found nothing" is a completed, non-error outcome (e.g. an empty search).
    if (tc.resultCount === 0) {
      return {
        id: tc.id,
        label,
        state: "skipped",
        meta,
        stateLabel: t("toolLabels.noResults", { defaultValue: "keine Ergebnisse" }),
      };
    }
    return { id: tc.id, label, state: "done", meta };
  });

  const failed = steps.filter((s) => s.state === "failed").length;
  let summary: string;
  if (live) {
    summary = deepRunning
      ? t("toolLabels.deepResearchRunning", {
          defaultValue: "Tiefenrecherche läuft … (dies dauert etwas länger)",
        })
      : t("toolLabels.working", { defaultValue: "Arbeitet …" });
    if (turnStart.current != null) summary += ` · ${formatElapsed(Date.now() - turnStart.current)}`;
  } else {
    summary = t("toolLabels.stepsDone", {
      count: steps.length,
      defaultValue: `${steps.length} ${steps.length === 1 ? "Arbeitsschritt" : "Arbeitsschritte"}`,
    });
    if (failed > 0) {
      summary += ` · ${t("toolLabels.stepsFailed", { count: failed, defaultValue: `${failed} fehlgeschlagen` })}`;
    }
  }

  // The flying-fact animation launches its pill from here when this turn added a
  // fact (see useFlyingFact): this row is what the operator is looking at when the
  // fact lands, so it is the honest origin.
  const carriesAddFact = toolCalls.some((tc) => tc.toolName === "add_fact");

  return (
    <div {...(carriesAddFact ? { "data-fact-flight-origin": "" } : {})}>
      <ChatSteps
        steps={steps}
        outcome={live ? "running" : "success"}
        open={open}
        onOpenChange={setOpen}
        labels={{
          summary,
          list: t("toolLabels.stepsList", { defaultValue: "Arbeitsschritte" }),
        }}
      />
    </div>
  );
}
